import React, { useState, useEffect, useRef } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AssistantWidget({ onOpenFull }) {
  const [open, setOpen] = useState(false);
  const [sid, setSid] = useState("");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setStreaming(true);
    try {
      let sessionId = sid;
      if (!sessionId) {
        const r = await axios.post(`${API}/assistant/sessions`, { title: "New conversation" });
        sessionId = r.data.id;
        setSid(sessionId);
      }
      setMessages((p) => [...p, { role: "user", content: text }, { role: "assistant", content: "" }]);
      const resp = await fetch(`${API}/assistant/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("ech_token")}` },
        body: JSON.stringify({ session_id: sessionId, message: text, mode: "ask" }),
      });
      if (!resp.ok) throw new Error((await resp.json()).detail || "Request failed");
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const ev = JSON.parse(part.slice(6));
          if (ev.delta) {
            setMessages((p) => {
              const n = [...p];
              n[n.length - 1] = { ...n[n.length - 1], content: n[n.length - 1].content + ev.delta };
              return n;
            });
          }
        }
      }
    } catch (e) {
      setMessages((p) => [...p.filter((m) => m.content !== ""), { role: "assistant", content: `Sorry — ${e.message || "the assistant is unavailable right now"}.` }]);
    }
    setStreaming(false);
  };

  return (
    <div className="ai-widget">
      {open && (
        <div className="ai-widget-panel" data-testid="ai-widget-panel">
          <div className="ai-widget-head">
            <span>✦ SOP Assistant</span>
            <span>
              <button className="ai-widget-link" onClick={() => { setOpen(false); onOpenFull(); }} data-testid="ai-widget-open-full">Full view</button>
              <button className="ai-widget-link" onClick={() => setOpen(false)} data-testid="ai-widget-close">✕</button>
            </span>
          </div>
          <div className="ai-widget-msgs" data-testid="ai-widget-messages">
            {messages.length === 0 && <p className="ai-widget-hint">Ask the manual anything — deadlines, gates, who owns what. Answers cite SOPs. Not legal advice.</p>}
            {messages.map((m, i) => (
              <div key={i} className={`ai-widget-msg ${m.role}`} data-testid={`ai-widget-msg-${m.role}`}>
                {m.content || (streaming && i === messages.length - 1 ? "…" : "")}
              </div>
            ))}
            <div ref={endRef} />
          </div>
          <div className="ai-widget-inputrow">
            <input className="inp" placeholder="Ask the manual…" value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") send(); }} disabled={streaming} data-testid="ai-widget-input" />
            <button className="btn" onClick={send} disabled={streaming || !input.trim()} data-testid="ai-widget-send">{streaming ? "…" : "Ask"}</button>
          </div>
        </div>
      )}
      <div className="ai-widget-row">
        {!open && (
          <button className="ai-widget-label" onClick={() => setOpen(true)} data-testid="ai-widget-label">AI Assist</button>
        )}
        <button className="ai-widget-fab" onClick={() => setOpen((o) => !o)} title="Ask the SOP Assistant" data-testid="ai-widget-fab">✦</button>
      </div>
    </div>
  );
}
